import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { ECTopbar } from './ECTopbar';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import { useState, useCallback, useMemo, useEffect } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { ECSettings } from './ECSettings';
import uuid from 'react-uuid';
import WarningIcon from '@mui/icons-material/Warning';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { isVacationReplyActiveFor } from './helpers/outOfOffice';
import moment from 'moment';
import { API_POST, DBRequest, useConfig } from '../../api/api';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Cookies from 'universal-cookie';
import { NewDefaultEmailIntegration } from '../new/NewDefaultEmailIntegration';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  foldersToTree,
  sortFolderTree,
  detectDelimiter,
} from './helpers/foldersToTree';
import ExoAlert from '../exo/ExoAlert';
import { useEmailClient } from './useEmailClient';
import { reauthenticateInbox } from './helpers/reauthenticate';

// Virtual folder with the drafts AI agents prepared through the MCP server.
export const AI_DRAFTS_API = 'ai-email-drafts';

export const folderOrder = [
  'INBOX',
  'Drafts',
  'Sent Items',
  'Spam',
  'Trash',
  'Archive',
];

export const ECInboxList = ({
  onOpen,
  inboxes,
  onSelect,
  onInboxChange,
  selectedFolder,
  folderMap,
  width,
  isLoading,
  onOpenSettings,
  // Jumps straight to the out of office tab of the settings dialog.
  onOpenOutOfOffice,
  // Opening a 1200x800 window only makes sense from the desktop layout, so the
  // pop-out button is opt-in rather than tied to another prop being present.
  canPopOut,
  hasDnd,
  isStandalone = false,
  onFolderCreate,
  onFolderDelete,
  onFolderMove,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  // smartfolder

  const smartFolders = [
    {
      id: 'S1',
      name: 'INBOX',
      type: 'INBOX',
      isOutgoing: false,
    },
    {
      id: 'S2',
      name: 'Sent Items',
      type: 'Sent Items',
      isOutgoing: true,
      api: 'imap-emails/dynamic/sent-folder',
      useDefaultApiForShow: true,
    },
    {
      id: 'S3',
      name: 'Outbound',
      type: 'Outbound',
      isOutgoing: true,
      api: 'outbound-emails',
      defaultFilter: 'queued',
    },
    {
      id: 'S4',
      name: 'AI Drafts',
      type: 'AI Drafts',
      isOutgoing: true,
      api: AI_DRAFTS_API,
    },
  ];

  function handleSmartFolderSelect(folder) {
    onSelect(folder);
  }

  return (
    <Box
      sx={{
        width,
      }}
      className="flex flex-col gap-2 relative justify-between h-full"
    >
      <Box className="flex flex-col gap-2 h-full overflow-y-auto w-full">
        <ECTopbar
          label={t('Inboxes')}
          ActionIcon={AddIcon}
          actionTooltip={t('Add E-Mail Account')}
          onAction={() => onOpen(true)}
        />
        {isLoading ? (
          <ExoSkeleton n={8} />
        ) : inboxes ? (
          <>
            <Box className="flex flex-col px-2 pb-4">
              <Typography
                fontSize="12px"
                fontWeight="600"
                className="opacity-50"
              >
                {t('All accounts')}
              </Typography>
              <Box className="flex flex-col">
                {smartFolders.map((folder) => (
                  <Folder
                    key={folder.id}
                    folder={folder}
                    onSelect={handleSmartFolderSelect}
                    selectedFolder={selectedFolder}
                    folderMap={folderMap}
                  />
                ))}
              </Box>
            </Box>
            {inboxes.map((inbox) => (
              <Inbox
                key={inbox.id}
                inbox={inbox}
                onSelect={onSelect}
                selectedFolder={selectedFolder}
                folderMap={folderMap}
                hasDnd={hasDnd}
                onInboxChange={onInboxChange}
                onFolderCreate={onFolderCreate}
                onFolderDelete={onFolderDelete}
                onFolderMove={onFolderMove}
                onOpenOutOfOffice={onOpenOutOfOffice}
              />
            ))}
          </>
        ) : (
          <>Add Account</>
        )}
      </Box>
      <Box className="flex gap-1">
        {canPopOut && !isStandalone && (
          <Tooltip title={t('Open in new window')}>
            <IconButton
              size="small"
              onClick={() =>
                window.open('/email-app', '_blank', 'width=1200,height=800')
              }
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onOpenSettings}>
          <SettingsIcon />
        </IconButton>
        {isStandalone && (
          <Tooltip title={t('Logout')}>
            <IconButton
              size="small"
              onClick={() => {
                if (window.standaloneLogout) {
                  window.standaloneLogout();
                }
              }}
              sx={{ color: colors.error }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const Inbox = ({
  inbox,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
  onInboxChange,
  onDelete,
  onFolderCreate,
  onFolderDelete,
  onFolderMove,
  onOpenOutOfOffice,
}) => {
  const theme = useTheme();
  const config = useConfig();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { label, color, imapUsername, imapStatus } = inbox;
  const { folders } = inbox.inbox;

  const { vacationNotification } = useEmailClient();
  const hasVacationReply =
    Boolean(onOpenOutOfOffice) &&
    isVacationReplyActiveFor(vacationNotification, inbox.id);

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  // re-auth popper
  const [reAuthenticateAnchorEl, setReAuthenticateAnchorEl] = useState(null);

  //To open new email integration dialog
  const [openNewEmailIntegration, setOpenNewEmailIntegration] = useState(false);
  const [selectedInbox, setSelectedInbox] = useState(null);

  // Convert to tree structure
  const { tree: folderTree } = useMemo(() => {
    return foldersToTree(folders);
  }, [folders]);

  // Sort the tree
  const sortedTree = useMemo(() => {
    return sortFolderTree(folderTree, folderOrder);
  }, [folderTree]);

  // Expanded state management with localStorage persistence
  const localStorageKey = `email-folders-expanded-${inbox.id}`;
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const saved = localStorage.getItem(localStorageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleExpand = useCallback(
    (folderId) => {
      setExpandedFolders((prev) => {
        const newState = { ...prev, [folderId]: !prev[folderId] };
        try {
          localStorage.setItem(localStorageKey, JSON.stringify(newState));
        } catch {
          // Ignore localStorage errors
        }
        return newState;
      });
    },
    [localStorageKey],
  );

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [createFolderDialog, setCreateFolderDialog] = useState(null);
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(null);

  // Alert state
  const [alert, setAlert] = useState({ open: false });

  // Handle delete click - check for children
  const handleDeleteClick = (folder) => {
    const hasChildren = folder.children?.length > 0;
    if (hasChildren) {
      setAlert({
        open: true,
        type: 'warning',
        message: t(
          'This folder contains subfolders and cannot be deleted. Please delete the subfolders first.',
        ),
      });
      return;
    }
    setDeleteFolderConfirm(folder);
  };

  // Handle folder delete confirmation
  const handleConfirmDelete = () => {
    if (deleteFolderConfirm && onFolderDelete) {
      onFolderDelete(deleteFolderConfirm);
    }
    setDeleteFolderConfirm(null);
  };

  // called from popper to re-authenticate
  function handleReAuthenticateInbox(inboxData) {
    reauthenticateInbox({
      config,
      inboxId: inboxData.id,
      onNoAuthUrl: () => setOpenNewEmailIntegration(true),
    });
  }

  return (
    <Box
      className="px-2 pb-4"
      sx={{
        '.dnd-heighlight .folder-button': {
          bgcolor: colors.info + '30 !important',
          border: `1px solid ${colors.info}80 !important`,
        },
      }}
    >
      <Typography
        fontSize={11}
        fontWeight={600}
        className="flex flex-col items-start gap-0"
      >
        {/* label and icon row */}
        <Box className="flex items-center gap-1">
          <span className="opacity-50">{label}</span>
          {imapStatus !== 1 && (
            <Tooltip title={t(`Reauthentication required`)}>
              <WarningIcon
                color="warning"
                sx={{
                  fontSize: 16,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  setReAuthenticateAnchorEl(e.target);
                  setSelectedInbox(inbox); // to know which inbox to re-auth
                }}
              />
            </Tooltip>
          )}
          <Tooltip title={t('Create folder')}>
            <IconButton
              size="small"
              onClick={() => setCreateFolderDialog({ isRoot: true })}
              sx={{ p: '2px', ml: 'auto' }}
            >
              <CreateNewFolderIcon sx={{ fontSize: 14, opacity: 0.5 }} />
            </IconButton>
          </Tooltip>
          {/* Only for mailboxes the vacation reply actually answers for, so the
              state is visible without opening settings. */}
          {hasVacationReply && (
            <Tooltip title={t('Automatic replies enabled')}>
              <IconButton
                size="small"
                onClick={onOpenOutOfOffice}
                sx={{ p: '2px' }}
              >
                <BeachAccessIcon sx={{ fontSize: 14, color: colors.success }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <span style={{ fontWeight: 400 }} className="opacity-50 pl-2 italic">
          {`<${imapUsername}>`}
        </span>
      </Typography>

      {/* Render folder tree */}
      <FolderTree
        folders={sortedTree}
        onSelect={onSelect}
        selectedFolder={selectedFolder}
        folderMap={folderMap}
        fromMove={fromMove}
        hasDnd={hasDnd}
        expandedFolders={expandedFolders}
        onToggleExpand={toggleExpand}
        onContextMenu={setContextMenu}
        depth={0}
        credentialsId={inbox.id}
        config={config}
      />

      {/* Context Menu */}
      <FolderContextMenu
        contextMenu={contextMenu}
        onClose={() => setContextMenu(null)}
        onCreateSubfolder={(folder) => {
          setCreateFolderDialog(folder);
          setContextMenu(null);
        }}
        onDeleteFolder={(folder) => {
          handleDeleteClick(folder);
          setContextMenu(null);
        }}
      />

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={Boolean(createFolderDialog)}
        parentFolder={createFolderDialog}
        inboxId={inbox.inbox.id}
        delimiter={detectDelimiter(folders)}
        onClose={() => setCreateFolderDialog(null)}
        onCreated={onFolderCreate}
      />

      {/* Delete Confirmation Dialog */}
      <ExoDialog
        open={Boolean(deleteFolderConfirm)}
        onClose={() => setDeleteFolderConfirm(null)}
        limitWidth
      >
        <Box className="p-4 flex flex-col gap-4">
          <Typography variant="h6">{t('Delete folder')}</Typography>
          <Typography>
            {t('Are you sure you want to delete the folder')}{' '}
            <strong>"{deleteFolderConfirm?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="error">
            {t('All emails in this folder will be permanently deleted.')}
          </Typography>
          <Box className="flex justify-end gap-2 mt-2">
            <Button onClick={() => setDeleteFolderConfirm(null)}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
            >
              {t('Delete')}
            </Button>
          </Box>
        </Box>
      </ExoDialog>

      {/* Alert for errors/warnings */}
      <ExoAlert
        alert={alert}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <Popover
        anchorEl={reAuthenticateAnchorEl}
        open={Boolean(reAuthenticateAnchorEl)}
        onClose={() => {
          setReAuthenticateAnchorEl(null);
          setSelectedInbox(null);
        }}
        disable
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className=" flex flex-col justify-center items-center w-full h-full gap-4 p-2 md:max-w-[350px]">
          <Typography fontSize={15} className="text-center">
            {t('Your connection has expired. Please sign in again.')}
          </Typography>
          <Button
            onClick={() => handleReAuthenticateInbox(selectedInbox)}
            variant="contained"
            color="info"
          >
            {t('Authenticate')}
          </Button>
        </Box>
      </Popover>
      <ExoDialog
        open={openNewEmailIntegration}
        onClose={() => {
          setOpenNewEmailIntegration(false);
          setReAuthenticateAnchorEl(null);
          setSelectedInbox(null);
        }}
        limitWidth
      >
        <NewDefaultEmailIntegration
          onChange={onInboxChange}
          startData={selectedInbox}
          userid={userId}
          onDelete={onDelete}
          onClose={() => {
            setOpenNewEmailIntegration(false);
            setReAuthenticateAnchorEl(null);
            setSelectedInbox(null);
          }}
        />
      </ExoDialog>
    </Box>
  );
};

// Recursive FolderTree component
const FolderTree = ({
  folders,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
  expandedFolders,
  onToggleExpand,
  onContextMenu,
  depth,
  credentialsId,
  config,
}) => {
  return (
    <Box sx={{ pl: depth > 0 ? 2 : 0 }}>
      {folders.map((folder) => (
        <FolderItem
          key={folder.id}
          folder={folder}
          onSelect={onSelect}
          selectedFolder={selectedFolder}
          folderMap={folderMap}
          fromMove={fromMove}
          hasDnd={hasDnd}
          isExpanded={expandedFolders[folder.id] ?? false}
          onToggleExpand={onToggleExpand}
          onContextMenu={onContextMenu}
          expandedFolders={expandedFolders}
          depth={depth}
          credentialsId={credentialsId}
          config={config}
        />
      ))}
    </Box>
  );
};

// Individual folder item with expand/collapse
const FolderItem = ({
  folder,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
  isExpanded,
  onToggleExpand,
  onContextMenu,
  expandedFolders,
  depth,
  credentialsId,
  config,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const hasChildren = folder.children && folder.children.length > 0;
  const { name, id, unseenCount, isVirtual } = folder;

  const Icon = folderMap[name]?.Icon || FolderIcon;
  const label = folderMap[name]?.label || name;
  const color = folderMap[name]?.color || 'unset';

  const isSelected = selectedFolder?.id === id;
  const formatedUnseenCount = unseenCount > 99 ? '99+' : unseenCount;

  function getDateLabel(dateTime) {
    const dateMoment = moment(dateTime);
    const now = moment();

    if (dateMoment.isSame(now, 'day')) {
      return [dateMoment.format('HH:mm'), true];
    } else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return [`${t('yesterday')} ${dateMoment.format('HH:mm')}`, false];
    } else {
      return [dateMoment.format(config?.general?.dateOutputFormat), false];
    }
  }
  const [dateLabel, isToday] = folder.lastSuccessfulSyncAt
    ? getDateLabel(folder.lastSuccessfulSyncAt)
    : ['', false];
  const tooltip =
    t(`This inbox is currently not synchronized.`) +
    (folder?.lastSuccessfulSyncAt
      ? ' ' +
        (isToday
          ? t(`Last successful sync was today at`)
          : t(`Last successful sync was at`)) +
        ' ' +
        `${dateLabel}`
      : '');

  // Right-click handler
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!isVirtual) {
      onContextMenu({
        mouseX: e.clientX,
        mouseY: e.clientY,
        folder,
        credentialsId,
      });
    }
  };

  const Container = hasDnd && !isVirtual ? DroppableContainer : Box;

  // Sortable for drag & drop folder reordering
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folder-sortable-${id}`,
    data: { type: 'folder', folder, credentialsId },
    disabled: isVirtual || !hasDnd,
  });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setSortableRef} style={sortableStyle}>
      <Container
        droppableId={`folder${folder.id}`}
        folder={folder}
        credentialsId={credentialsId}
      >
        <Box className="flex items-center">
          {/* Expand/Collapse button */}
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(id);
              }}
              sx={{ p: '2px' }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: '16px',
                  transform: `rotate(${isExpanded ? '0deg' : '-90deg'})`,
                  transition: '0.2s',
                }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: '24px' }} /> // Spacer for alignment
          )}

          {/* Folder button */}
          <Button
            onClick={() => !isVirtual && onSelect(folder)}
            onContextMenu={handleContextMenu}
            disabled={isVirtual}
            {...(hasDnd && !isVirtual ? { ...attributes, ...listeners } : {})}
            sx={{
              flex: 1,
              padding: '3px 10px',
              justifyContent: 'start',
              borderRadius: '5px',
              bgcolor: isSelected && !fromMove ? colors.card : 'transparent',
              border: `1px solid transparent`,
              opacity: isVirtual ? 0.6 : 1,
              cursor: isVirtual ? 'default' : 'pointer',
              ':hover': {
                bgcolor: isVirtual ? 'transparent' : colors.info + '30',
                border: isVirtual
                  ? '1px solid transparent'
                  : `1px solid ${colors.info}80`,
              },
              '.icon': { fontSize: '16px' },
              '@media (max-width: 1024px)': {
                height: '60px',
                '.text': {
                  fontSize: '18px',
                },
                '.icon': {
                  fontSize: '20px',
                },
              },
            }}
            className="folder-button"
          >
            <Box className="flex w-full gap-2 items-center">
              <Icon sx={{ color }} className="opacity-50 icon" />
              <Typography className="text">{label}</Typography>
            </Box>
            {!folder?.syncStatus?.errorEncountered && unseenCount > 0 && (
              <Typography
                fontSize={10}
                fontWeight={500}
                sx={{
                  bgcolor: colors.info + '30',
                  border: `1px solid ${colors.info}80`,
                }}
                className="px-1 rounded-full"
              >
                {formatedUnseenCount}
              </Typography>
            )}
            {!isVirtual &&
              (folder?.syncStatus?.errorEncountered ||
                moment(folder?.lastSuccessfulSyncAt).isBefore(
                  moment().subtract(1, 'hour'),
                )) && (
                <Tooltip title={tooltip}>
                  <WarningIcon color="warning" sx={{ fontSize: '16px' }} />
                </Tooltip>
              )}
          </Button>
        </Box>
      </Container>

      {/* Children */}
      <Collapse in={isExpanded}>
        {hasChildren && (
          <FolderTree
            folders={folder.children}
            onSelect={onSelect}
            selectedFolder={selectedFolder}
            folderMap={folderMap}
            fromMove={fromMove}
            hasDnd={hasDnd}
            expandedFolders={expandedFolders}
            onToggleExpand={onToggleExpand}
            onContextMenu={onContextMenu}
            depth={depth + 1}
            credentialsId={credentialsId}
            config={config}
          />
        )}
      </Collapse>
    </Box>
  );
};

// Original Folder component for smart folders and simple cases
export const Folder = ({
  folder,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const config = useConfig();

  const { t } = useTranslation();
  const { failedCount, queuedCount, aiDraftCount } = useEmailClient();

  const { name, id, unseenCount } = folder;
  const isOutbound = folder.type === 'Outbound';
  const isAiDrafts = folder.api === AI_DRAFTS_API;

  const Icon = folderMap[name]?.Icon || FolderIcon;
  const label = folderMap[name]?.label || name;
  const color = folderMap[name]?.color || 'unset';

  const isSelected = selectedFolder?.id === id;

  const formatedUnseenCount = unseenCount > 99 ? '99+' : unseenCount;

  function getDateLabel(dateTime) {
    const dateMoment = moment(dateTime);
    const now = moment();

    if (dateMoment.isSame(now, 'day')) {
      return [dateMoment.format('HH:mm'), true];
    } else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return [`${t('yesterday')} ${dateMoment.format('HH:mm')}`, false];
    } else {
      return [dateMoment.format(config?.general?.dateOutputFormat), false];
    }
  }
  const [dateLabel, isToday] = folder.lastSuccessfulSyncAt
    ? getDateLabel(folder.lastSuccessfulSyncAt)
    : ['', false];
  const tooltip =
    t(`This inbox is currently not synchronized.`) +
    (folder?.lastSuccessfulSyncAt
      ? ' ' +
        (isToday
          ? t(`Last successful sync was today at`)
          : t(`Last successful sync was at`)) +
        ' ' +
        `${dateLabel}`
      : '');

  const Container = hasDnd ? DroppableContainer : Box;

  return (
    <Container droppableId={`folder${folder.id}`} folder={folder}>
      <Button
        onClick={() => onSelect(folder)}
        sx={{
          padding: '3px 10px',
          justifyContent: 'start',
          borderRadius: '5px',
          bgcolor: isSelected && !fromMove ? colors.card : 'transparent',
          border: `1px solid transparent`,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
          '.icon': { fontSize: '16px' },
          '@media (max-width: 1024px)': {
            height: '60px',
            '.text': {
              fontSize: '18px',
            },
            '.icon': {
              fontSize: '20px',
            },
          },
        }}
        className="w-full flex gap-2 folder-button"
      >
        <Box className="flex w-full gap-2 items-center">
          <Icon sx={{ color }} className="opacity-50 icon" />
          <Typography className="text">{label}</Typography>
        </Box>
        {!folder?.syncStatus?.errorEncountered && unseenCount > 0 && (
          <Typography
            fontSize={10}
            fontWeight={500}
            sx={{
              bgcolor: colors.info + '30',
              border: `1px solid ${colors.info}80`,
            }}
            className="px-1 rounded-full"
          >
            {formatedUnseenCount}
          </Typography>
        )}
        {isOutbound && queuedCount > 0 && (
          <Tooltip title={t('Queued emails')}>
            <Typography
              fontSize={10}
              fontWeight={500}
              sx={{
                bgcolor: colors.warning + '30',
                border: `1px solid ${colors.warning}80`,
              }}
              className="px-1 rounded-full"
            >
              {queuedCount > 99 ? '99+' : queuedCount}
            </Typography>
          </Tooltip>
        )}
        {isAiDrafts && aiDraftCount > 0 && (
          <Tooltip title={t('AI drafts waiting for your review')}>
            <Typography
              fontSize={10}
              fontWeight={500}
              sx={{
                bgcolor: colors.info + '30',
                border: `1px solid ${colors.info}80`,
              }}
              className="px-1 rounded-full"
            >
              {aiDraftCount > 99 ? '99+' : aiDraftCount}
            </Typography>
          </Tooltip>
        )}
        {isOutbound && failedCount > 0 && (
          <Tooltip title={t('Failed emails')}>
            <Typography
              fontSize={10}
              fontWeight={500}
              sx={{
                bgcolor: colors.error + '30',
                border: `1px solid ${colors.error}80`,
              }}
              className="px-1 rounded-full"
            >
              {failedCount > 99 ? '99+' : failedCount}
            </Typography>
          </Tooltip>
        )}
        {(folder?.syncStatus?.errorEncountered ||
          moment(folder?.lastSuccessfulSyncAt).isBefore(
            moment().subtract(1, 'hour'),
          )) && (
          <Tooltip title={tooltip}>
            <WarningIcon color="warning" />
          </Tooltip>
        )}
      </Button>
    </Container>
  );
};

// Context Menu for folder operations
const FolderContextMenu = ({
  contextMenu,
  onClose,
  onCreateSubfolder,
  onDeleteFolder,
}) => {
  const { t } = useTranslation();

  if (!contextMenu) return null;

  return (
    <Menu
      open={Boolean(contextMenu)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      <MenuItem
        onClick={() => {
          onCreateSubfolder(contextMenu.folder);
        }}
      >
        <ListItemIcon>
          <CreateNewFolderIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('Create subfolder')}</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          onDeleteFolder(contextMenu.folder);
        }}
      >
        <ListItemIcon>
          <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
        </ListItemIcon>
        <ListItemText sx={{ color: 'error.main' }}>
          {t('Delete folder')}
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

// Create Folder Dialog
const CreateFolderDialog = ({
  open,
  parentFolder,
  inboxId,
  delimiter,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [folderName, setFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFolderName('');
      setError(null);
    }
  }, [open]);

  const handleCreate = () => {
    if (!folderName.trim()) return;

    setIsLoading(true);
    setError(null);

    const parentPath = parentFolder?.path || '';

    DBRequest({
      config,
      path: 'email-folders',
      method: API_POST({
        inboxId,
        name: folderName.trim(),
        parentPath,
      }),
      onResponse: (res) => {
        setIsLoading(false);
        setFolderName('');
        onCreated?.(res.data);
        onClose();
      },
      onError: (err) => {
        setIsLoading(false);
        setError(err.message || t('Failed to create folder'));
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ '.MuiDialog-paper': { maxWidth: '450px' } }}
    >
      <DialogTitle>
        {parentFolder && parentFolder.name
          ? `${t('Create subfolder in')} "${parentFolder.name}"`
          : t('Create new folder')}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label={t('Folder name')}
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
          error={Boolean(error)}
          helperText={error}
          disabled={isLoading}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
          color="error"
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleCreate}
          disabled={isLoading || !folderName.trim()}
        >
          {isLoading ? <CircularProgress size={20} /> : t('Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DroppableContainer = ({
  droppableId,
  children,
  folder,
  credentialsId,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { ...folder, credentialsId },
  });
  return (
    <Box
      ref={setNodeRef}
      key={droppableId}
      className={isOver ? 'dnd-heighlight' : ''}
    >
      {children}
    </Box>
  );
};
